import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '@/utils/cn'

type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive'
type Size = 'sm' | 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  icon?: React.ReactNode
}

const variantClasses: Record<Variant, string> = {
  primary: [
    'bg-[var(--brand-primary)] text-white font-medium',
    'hover:bg-[var(--brand-primary-hover)]',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'transition-colors duration-150',
  ].join(' '),

  secondary: [
    'bg-[var(--glass-bg)] text-[var(--text-primary)]',
    'border border-[var(--glass-border)]',
    'hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)]',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'transition-all duration-150',
  ].join(' '),

  ghost: [
    'bg-transparent text-[var(--text-secondary)]',
    'hover:bg-[var(--glass-bg)] hover:text-[var(--text-primary)]',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'transition-all duration-150',
  ].join(' '),

  destructive: [
    'bg-[var(--color-danger,#e5484d)] text-white font-medium',
    'hover:opacity-90',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'transition-opacity duration-150',
  ].join(' '),
}

const sizeClasses: Record<Size, string> = {
  sm: 'text-xs px-3 py-1.5 rounded-md gap-1.5 min-h-[44px]',
  md: 'text-[13px] px-4 py-2 rounded-md gap-2 min-h-[44px]',
  lg: 'text-sm px-5 py-2.5 rounded-lg gap-2 min-h-[44px]',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      icon,
      children,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          'inline-flex items-center justify-center cursor-pointer select-none',
          variantClasses[variant],
          sizeClasses[size],
          className
        )}
        {...props}
      >
        {loading ? (
          <span className='w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin' />
        ) : (
          icon
        )}
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'
